import {
  ContactCreateParams,
  MappedField,
  Program,
  ProgramAttendeeCreateParams,
} from '@citruscamps/citrus-client'
import { faArrowLeft } from '@fortawesome/pro-duotone-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { default as React, useState } from 'react'
import { ConfirmModal } from '../../../../../components/ConfirmModal/ConfirmModal'
import { ErrorContent } from '../../../../../components/ErrorContent/ErrorContent'
import { useToast } from '../../../../../hooks/useToast'
import translations from '../../../../../translations/en.json'
import { formatList } from '../../../../../utils/formatters'
import { AttendeeMatchStep } from '../../../../Attendees/pages/AttendeeImport/components/AttendeeMatchStep'
import { ContactMatchStep } from '../../../../Contacts/pages/ContactImport/components/ContactMatchStep'
import {
  AmountOfObjects,
  BatchImportOperation,
  IBulkImportCreateOptions,
  IParseFileImportOptions,
  ImportItemType,
} from '../hooks/useHandleImport'
import { useImportWizard } from '../hooks/useImportWizard'
import { ChooseAmountofObjectsStep } from './ChooseAmountofObjectsStep'
import { ChooseItemTypeStep } from './ChooseItemTypeStep'
import { CompleteImportStep } from './CompleteImportStep'
import { CsvImportForm } from './CsvImportForm'
import { ReviewImportStep } from './ReviewImportStep'

interface IProps {
  program: Program
  onUploadFile: (options: IParseFileImportOptions) => Promise<MappedField[]>
  onSubmit: (values: IBulkImportCreateOptions) => Promise<BatchImportOperation>
}

export const ImportWizard = ({
  program,
  onUploadFile: handleUploadFile,
  onSubmit: handleSubmit,
}: IProps): React.ReactElement => {
  const { setToast } = useToast()
  const [amountOfObjects, setAmountOfObjects] = useState<AmountOfObjects>()
  const [itemTypes, setItemTypes] = useState<ImportItemType[]>([])
  const [file, setFile] = useState<File | undefined>()
  const [fields, setFields] = useState<MappedField[]>([])
  const [contacts, setContacts] = useState<ContactCreateParams[]>([])
  const [attendees, setAttendees] = useState<ProgramAttendeeCreateParams[]>([])
  const [operation, setOperation] = useState<BatchImportOperation>()
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false)
  const { step, nextStep, previousStep, reset } = useImportWizard()

  if (step === 'choose_amount_of_objects') {
    return (
      <ChooseAmountofObjectsStep
        className="py-3 w-lg-75"
        initialValues={amountOfObjects && { amount: amountOfObjects }}
        onSubmit={({ amount: val }) => {
          setAmountOfObjects(val)
          nextStep()
        }}
      />
    )
  }

  if (step === 'choose_object_types' && !!amountOfObjects) {
    return (
      <ChooseItemTypeStep
        className="py-3 w-lg-75"
        amount={amountOfObjects}
        types={itemTypes}
        onBack={(val) => {
          setItemTypes(val)
          previousStep()
        }}
        onSubmit={(val) => {
          setItemTypes(val)
          nextStep()
        }}
      />
    )
  }

  if (step === 'import_csv') {
    return (
      <CsvImportForm
        initialValues={{ file }}
        entityType={formatList(itemTypes, 'and')}
        onBack={() => previousStep()}
        onSubmit={(values: MappedField[], fileVal?: File) => {
          setFields(values)
          setFile(fileVal)
          if (values.length > 0 && fileVal) {
            nextStep()
          }
        }}
        onUploadFile={(file) => handleUploadFile({ file, itemTypes })}
      />
    )
  }

  if (step === 'match_fields' && itemTypes.includes('contacts')) {
    return (
      <ContactMatchStep
        initialValues={{ fields }}
        program={program}
        onBack={() => previousStep()}
        onSubmit={async (values: ContactCreateParams[], fieldVals?: MappedField[]) => {
          setContacts(values || [])
          setFields(fieldVals || [])
          nextStep()
        }}
      />
    )
  }

  if (step === 'match_fields' && itemTypes.includes('attendees')) {
    return (
      <AttendeeMatchStep
        initialValues={{ fields }}
        programId={program.id}
        onBack={() => previousStep()}
        onSubmit={async (values: ProgramAttendeeCreateParams[], fieldVals?: MappedField[]) => {
          setAttendees(values || [])
          setFields(fieldVals || [])
          nextStep()
        }}
      />
    )
  }

  if (step === 'review_import') {
    return (
      <>
        <ReviewImportStep
          program={program}
          itemTypes={itemTypes}
          initialValues={{ itemTypes, contacts, attendees }}
          onBack={() => previousStep()}
          onSubmit={async (values: Omit<IBulkImportCreateOptions, 'itemTypes'>) => {
            const sendEmail = values.contacts.some((v) => v.send_invite)
            if (sendEmail) {
              setContacts(values.contacts)
              setAttendees(values.attendees)
              setShowConfirmation(true)
            } else {
              const data = await handleSubmit?.({
                itemTypes,
                contacts: values.contacts,
                attendees: values.attendees,
                registrations: [],
                events: [],
                notes: [],
              })
              setOperation(data)
              nextStep()
            }
          }}
        />
        <ConfirmModal
          title="Confirm send invites"
          show={showConfirmation}
          onClose={() => setShowConfirmation(false)}
          onSubmit={async () => {
            if (!!contacts) {
              const data = await handleSubmit?.({
                itemTypes,
                contacts,
                attendees,
                registrations: [],
                events: [],
                notes: [],
              })
              setOperation(data)
              setShowConfirmation(false)
              nextStep()
            } else {
              setToast({
                header: (translations as any)['error.default_toast_header'],
                message: (translations as any)['error.default_toast_message'],
                type: 'danger',
              })
            }
          }}
        >
          You are going to send emails to{' '}
          <b>{contacts?.length || attendees?.length || []} contacts</b>. If you are sure, please
          confirm this action.
        </ConfirmModal>
      </>
    )
  }

  if (step === 'complete_import') {
    return (
      <CompleteImportStep
        itemTypes={itemTypes}
        operation={operation}
        onRetry={() => {
          previousStep(2)
        }}
        onClear={() => {
          reset()
          setFile(undefined)
          setFields([])
          setContacts([])
          setAttendees([])
          setOperation(undefined)
        }}
      />
    )
  }

  return (
    <div className="w-lg-75 mx-auto">
      <ErrorContent size="lg">
        <button className="btn btn-outline-primary" onClick={() => previousStep()}>
          <FontAwesomeIcon className="me-2" icon={faArrowLeft} />
          Go back
        </button>
      </ErrorContent>
    </div>
  )
}
